import React, { useState } from 'react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';

const PhaseTransitionModal = ({ 
  pendingTransition, 
  onSave, 
  onClose,
  isSaving = false
}) => {
  const { phaseCompleted, phaseStarting, dayNumber, summary } = pendingTransition;
  
  const [reflection, setReflection] = useState({
    mostChanged: '',
    biggestChallenge: '',
    valuableInsight: '',
    excitedAbout: '',
    commitment: ''
  });
  
  const [currentStep, setCurrentStep] = useState(0); // 0: celebration, 1: stats, 2: reflection, 3: next phase
  
  const phaseNames = {
    1: 'Foundation - Daily Accountability',
    2: 'Deepening - Pattern Recognition',
    3: 'Integration - Wellness Maintenance',
    4: 'Mastery - Personal Wellness Playbook'
  };
  
  const phaseFocus = {
    2: 'Over the next 90 days, you\'ll dive deeper into understanding the patterns and triggers that shape your journey. This phase is about recognition and insight.',
    3: 'The next 90 days focus on integration. You\'ll build a comprehensive wellness maintenance strategy and strengthen your long-term mental wellness tools.',
    4: 'Welcome to the final phase. Over the next 95 days, you\'ll develop your Personal Wellness Playbook - a guide you\'ve written for yourself, by yourself.'
  };
  
  const handleChange = (field, value) => {
    setReflection(prev => ({ ...prev, [field]: value }));
  };
  
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSubmit = () => {
    onSave({
      phaseCompleted,
      phaseStarting,
      dayNumber,
      summary,
      reflection
    });
  };
  
  const isReflectionComplete = () => {
    return reflection.mostChanged && 
           reflection.biggestChallenge && 
           reflection.valuableInsight && 
           reflection.excitedAbout && 
           reflection.commitment;
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <h2 className="text-3xl font-bold mb-2">
            🎉 Phase {phaseCompleted} Complete!
          </h2>
          <p className="text-blue-100">
            You've completed {dayNumber - 1} days of mental wellness commitment. This is a significant milestone.
          </p>
        </div>
        
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 py-4 px-6 bg-gray-50">
          {[0, 1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                step === currentStep 
                  ? 'bg-blue-600 text-white' 
                  : step < currentStep 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {step < currentStep ? '✓' : step + 1}
              </div>
              {step < 3 && (
                <div className={`w-12 h-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-300'}`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Step 0: Celebration */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Congratulations on Completing Phase {phaseCompleted}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                You've just completed <strong>{phaseNames[phaseCompleted]}</strong>. 
                That's 90 days of showing up, doing the work, and choosing your mental wellness each day.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This moment matters because progress isn't a straight line, and you've proven 
                to yourself that you can build consistency even when it's hard.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Let's take a moment to reflect on what this phase meant for you, and then 
                we'll orient toward Phase {phaseStarting}.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-blue-900 font-medium">
                  "Mental wellness is not about perfection. It's about progress, one day at a time."
                </p>
              </div>
            </div>
          )}
          
          {/* Step 1: Stats Summary */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Your Phase {phaseCompleted} By The Numbers
              </h3>
              <p className="text-gray-700">
                Here's what you accomplished over the last 90 days:
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                  <div className="text-3xl font-bold text-green-700">
                    {summary.totalDaysCompleted}
                  </div>
                  <div className="text-sm text-green-600 mt-1">Days Completed</div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                  <div className="text-3xl font-bold text-purple-700">
                    {summary.journalEntriesWritten}
                  </div>
                  <div className="text-sm text-purple-600 mt-1">Journal Entries</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <div className="text-3xl font-bold text-blue-700">
                    {summary.averageMood !== null ? summary.averageMood.toFixed(1) : 'N/A'}
                  </div>
                  <div className="text-sm text-blue-600 mt-1">Average Mood</div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                  <div className="text-3xl font-bold text-orange-700">
                    {summary.averageCraving !== null ? summary.averageCraving.toFixed(1) : 'N/A'}
                  </div>
                  <div className="text-sm text-orange-600 mt-1">Average Challenge</div>
                </div>
                
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                  <div className="text-3xl font-bold text-indigo-700">
                    {summary.longestStreak}
                  </div>
                  <div className="text-sm text-indigo-600 mt-1">Longest Streak</div>
                </div>
                
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg border border-teal-200">
                  <div className="text-3xl font-bold text-teal-700">
                    {summary.activitiesCompleted}
                  </div>
                  <div className="text-sm text-teal-600 mt-1">Activities Completed</div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <p className="text-gray-700 text-sm">
                  These numbers represent real effort, real commitment, and real growth. 
                  Every entry, every activity, every day you showed up contributed to where you are now.
                </p>
              </div>
            </div>
          )}
          
          {/* Step 2: Reflection Prompts */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Growth Reflection
              </h3>
              <p className="text-gray-700 mb-4">
                Take a few minutes to reflect on Phase {phaseCompleted}. Your answers will be saved 
                and visible to you (and your clinician if you've granted access).
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What changed the most for you during this phase?
                  </label>
                  <Textarea
                    value={reflection.mostChanged}
                    onChange={(e) => handleChange('mostChanged', e.target.value)}
                    placeholder="Think about shifts in mindset, habits, relationships, or emotional patterns..."
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{reflection.mostChanged.length}/500</div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What was your biggest challenge, and how did you face it?
                  </label>
                  <Textarea
                    value={reflection.biggestChallenge}
                    onChange={(e) => handleChange('biggestChallenge', e.target.value)}
                    placeholder="Every phase has hard moments. What was yours, and what did you learn?"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{reflection.biggestChallenge.length}/500</div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What's the most valuable insight you gained?
                  </label>
                  <Textarea
                    value={reflection.valuableInsight}
                    onChange={(e) => handleChange('valuableInsight', e.target.value)}
                    placeholder="What did you learn about yourself, mental wellness, or what works for you?"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{reflection.valuableInsight.length}/500</div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What are you most excited about as you enter Phase {phaseStarting}?
                  </label>
                  <Textarea
                    value={reflection.excitedAbout}
                    onChange={(e) => handleChange('excitedAbout', e.target.value)}
                    placeholder="What possibilities, goals, or changes are you looking forward to?"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{reflection.excitedAbout.length}/500</div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What's one commitment you're making to yourself for the next 90 days?
                  </label>
                  <Textarea
                    value={reflection.commitment}
                    onChange={(e) => handleChange('commitment', e.target.value)}
                    placeholder="This can be specific or general, big or small. What matters is that it's yours."
                    rows={3}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{reflection.commitment.length}/500</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Next Phase Introduction */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Welcome to Phase {phaseStarting}
              </h3>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-500">
                <h4 className="font-bold text-lg text-indigo-900 mb-2">
                  {phaseNames[phaseStarting]}
                </h4>
                <p className="text-indigo-800">
                  {phaseFocus[phaseStarting]}
                </p>
              </div>
              
              <div className="space-y-3 mt-6">
                <h4 className="font-semibold text-gray-900">What's Changing:</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  {phaseStarting === 2 && (
                    <>
                      <li>Content will focus more on identifying patterns and triggers</li>
                      <li>Journal prompts will go deeper into self-reflection</li>
                      <li>Move activities may increase in complexity and duration</li>
                      <li>You'll start building awareness of your personal warning signs</li>
                    </>
                  )}
                  {phaseStarting === 3 && (
                    <>
                      <li>Integration of all skills learned so far into daily life</li>
                      <li>Development of your personal wellness maintenance plan</li>
                      <li>Deeper work on challenging situations and coping strategies</li>
                      <li>Emphasis on sustainable, long-term mental wellness patterns</li>
                    </>
                  )}
                  {phaseStarting === 4 && (
                    <>
                      <li>You'll create your Personal Wellness Playbook</li>
                      <li>Focus shifts to long-term maintenance and thriving</li>
                      <li>Content becomes more self-directed and reflective</li>
                      <li>Preparation for life beyond the 365-day program</li>
                    </>
                  )}
                </ul>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mt-6">
                <p className="text-yellow-900 text-sm">
                  <strong>Remember:</strong> You don't have to be perfect in this phase. 
                  You just have to keep showing up. The program meets you where you are.
                </p>
              </div>
              
              <div className="mt-6 pt-4 border-t">
                <p className="text-gray-700 font-medium">
                  Ready to begin Phase {phaseStarting}?
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  Your Day {dayNumber} content is waiting for you.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex items-center justify-between">
          <div>
            {currentStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                disabled={isSaving}
              >
                ← Back
              </Button>
            )}
          </div>
          
          <div className="flex gap-2">
            {currentStep < 2 && (
              <Button onClick={handleNext}>
                Next →
              </Button>
            )}
            
            {currentStep === 2 && (
              <Button 
                onClick={handleNext}
                disabled={!isReflectionComplete()}
              >
                Next →
              </Button>
            )}
            
            {currentStep === 3 && (
              <Button 
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Begin Phase ' + phaseStarting}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhaseTransitionModal;
